import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Switch } from '@/components/ui/Switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/Dialog'

export interface InboxFormData {
  name: string
  active: boolean
  follow_up_enabled: boolean
  follow_up_delay_minutes: number
}

interface InboxFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  formData: InboxFormData
  onFormChange: (data: InboxFormData) => void
  onSubmit: () => void
  isEditing: boolean
}

export function InboxFormDialog({
  open,
  onOpenChange,
  formData,
  onFormChange,
  onSubmit,
  isEditing,
}: InboxFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Inbox' : 'Create Inbox'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="inbox-name">Inbox Name *</Label>
            <Input
              id="inbox-name"
              value={formData.name}
              onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
              placeholder="e.g., Sales, Support, Billing"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <Label htmlFor="inbox-active">Active</Label>
            <Switch
              id="inbox-active"
              checked={formData.active}
              onCheckedChange={(checked) => onFormChange({ ...formData, active: checked })}
            />
          </div>

          {isEditing && (
            <div className="pt-2 space-y-4 border-t border-border">
              <div>
                <p className="text-sm font-medium">Follow-up Email</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Email visitors after an agent replies if they've left your site, so they don't miss the response. Skipped while the visitor is still active on your site, where they see the reply in the widget in real time. Only sent if the visitor has an email address on record.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="inbox-follow-up-enabled">Enable follow-up emails</Label>
                <Switch
                  id="inbox-follow-up-enabled"
                  checked={formData.follow_up_enabled}
                  onCheckedChange={(checked) => onFormChange({ ...formData, follow_up_enabled: checked })}
                />
              </div>

              <div className={`flex items-center gap-2 ${!formData.follow_up_enabled ? 'opacity-50' : ''}`}>
                <Label htmlFor="inbox-follow-up-delay" className="whitespace-nowrap">Send after</Label>
                <Input
                  id="inbox-follow-up-delay"
                  type="number"
                  min={1}
                  max={1440}
                  value={formData.follow_up_delay_minutes}
                  onChange={(e) => onFormChange({ ...formData, follow_up_delay_minutes: parseInt(e.target.value) || 3 })}
                  disabled={!formData.follow_up_enabled}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">minutes of no reply</span>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            Cancel
          </Button>
          <Button onClick={onSubmit} className="cursor-pointer">
            {isEditing ? 'Save Changes' : 'Create Inbox'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
